import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { isMobileDevice } from 'utils';
import { KEY } from '../utils/constants';

/**
 * useGigabotPanel Hook
 *
 * Generic, reusable hook to manage Gigabot chat panel state and interactions.
 * Can be used on any page (Profile, Contracts, Prompts, etc.) with custom context.
 *
 * @param {Object} options - Configuration options
 * @param {string} options.context - Page context (e.g., 'profile', 'contracts', 'prompts')
 * @param {Object} options.contextData - Context-specific data to send with chat messages
 * @param {Function} options.onActionRun - Callback when action proposal is run
 * @param {Function} options.onActionSkip - Callback when action proposal is skipped
 * @param {Function} options.generateInitialMessage - Function to generate initial welcome message
 * @param {string} options.containerSelector - CSS selector for container to adjust padding on resize
 * @param {boolean} options.enableKeyboardShortcuts - Enable Cmd+L keyboard shortcut (default: true)
 * @param {boolean} options.startOpen - Start with Gigabot open (default: !isMobileDevice())
 *
 * @returns {Object} Gigabot panel state and methods
 *
 * @example
 * // Profile page usage
 * const gigabot = useGigabotPanel({
 *   context: 'profile',
 *   contextData: profileData,
 *   onActionRun: async (proposal) => {
 *     if (proposal.type === 'profile_update') {
 *       updateProfile(proposal.changes);
 *     }
 *   },
 *   generateInitialMessage: () => ({
 *     content: `Hi! I can help optimize your profile...`,
 *   }),
 * });
 *
 * @example
 * // Contracts page usage
 * const gigabot = useGigabotPanel({
 *   context: 'contracts',
 *   contextData: { contracts, selectedContract },
 *   onActionRun: async (proposal) => {
 *     if (proposal.type === 'api_call') {
 *       await createContract(proposal.preview);
 *     }
 *   },
 *   generateInitialMessage: () => ({
 *     content: `Hi! I can help you manage contracts...`,
 *   }),
 * });
 *
 * @example
 * // Prompts page usage
 * const gigabot = useGigabotPanel({
 *   context: 'prompts',
 *   contextData: { prompts, activePrompt },
 *   onActionRun: async (proposal) => {
 *     if (proposal.type === 'command') {
 *       await executeCommand(proposal.command);
 *     }
 *   },
 * });
 */
export const useGigabotPanel = ({
  context = 'default',
  contextData = null,
  onActionRun = null,
  onActionSkip = null,
  generateInitialMessage = null,
  containerSelector = null,
  enableKeyboardShortcuts = true,
  startOpen = !isMobileDevice(),
} = {}) => {
  const [showGigabot, setShowGigabot] = useState(startOpen);
  const initialMessageRef = useRef(null);
  const contextDataRef = useRef(null);

  /**
   * Handle Gigabot close
   */
  const handleGigabotClose = useCallback(() => {
    setShowGigabot(false);
    // Reset cached data when closing
    initialMessageRef.current = null;
    contextDataRef.current = null;
  }, []);

  /**
   * Handle Gigabot open
   */
  const handleGigabotOpen = useCallback(() => {
    setShowGigabot(true);
  }, []);

  /**
   * Create initial message for Gigabot
   * Only created once when Gigabot opens (persists during updates)
   */
  const gigabotInitialMessage = useMemo(() => {
    // If message already exists, return it (chat persists)
    if (initialMessageRef.current) {
      return initialMessageRef.current;
    }

    // Create new message only on first open
    if (showGigabot) {
      let message;

      if (generateInitialMessage) {
        // Use custom message generator if provided
        const customMessage = generateInitialMessage(contextData);
        message = {
          id: 1,
          type: 'assistant',
          timestamp: new Date(),
          ...customMessage,
        };
      } else {
        // Default generic message
        message = {
          id: 1,
          type: 'assistant',
          content: `Hi! I'm Gigabot, your AI assistant. How can I help you today?`,
          timestamp: new Date(),
        };
      }

      initialMessageRef.current = message;
      return message;
    }

    return null;
  }, [showGigabot, generateInitialMessage]); // Only depends on showGigabot, NOT on context data

  /**
   * Handle action proposal run
   * Delegates to the provided callback
   */
  const handleActionRun = useCallback(
    async (proposal) => {
      if (onActionRun) {
        try {
          await onActionRun(proposal);
        } catch (error) {
          console.error('Error running action proposal:', error);
          throw error;
        }
      } else {
        console.warn('No onActionRun handler provided');
      }
    },
    [onActionRun]
  );

  /**
   * Handle action proposal skip
   * Delegates to the provided callback
   */
  const handleActionSkip = useCallback(
    (proposal) => {
      if (onActionSkip) {
        onActionSkip(proposal);
      } else {
        console.log('Skipped proposal:', proposal.id);
      }
    },
    [onActionSkip]
  );

  /**
   * Update context data reference when context data changes
   * Keeps same object reference to prevent re-renders
   */
  useEffect(() => {
    if (showGigabot && contextData) {
      if (!contextDataRef.current) {
        // Create initial context object
        contextDataRef.current = { ...contextData };
      } else {
        // Update existing object properties (keeps same reference)
        Object.keys(contextData).forEach((key) => {
          contextDataRef.current[key] = contextData[key];
        });
      }
    }
  }, [showGigabot, contextData]);

  /**
   * Gigabot context data
   */
  const gigabotContextData = contextDataRef.current;

  /**
   * Gigabot ARIA props
   */
  const gigabotAriaProps = useMemo(
    () => ({
      id: 'gigabot-chat',
      role: 'dialog',
      'aria-modal': 'false',
      'aria-label': `Gigabot chat panel for ${context}`,
      'aria-hidden': !showGigabot,
    }),
    [showGigabot, context]
  );

  /**
   * Handle Gigabot resize
   * Adjusts container padding to accommodate Gigabot panel
   */
  const handleGigabotResize = useCallback(
    (width) => {
      if (containerSelector) {
        const container = document.querySelector(containerSelector);
        if (container) {
          container.style.paddingRight = `calc(${width}px + 2rem)`;
        }
      }
    },
    [containerSelector]
  );

  /**
   * Reset container padding when Gigabot closes
   */
  useEffect(() => {
    if (containerSelector) {
      const container = document.querySelector(containerSelector);
      if (container && !showGigabot) {
        // Reset padding when Gigabot is closed
        container.style.paddingRight = '';
      }
    }
  }, [showGigabot, containerSelector]);

  /**
   * Global keyboard shortcuts (Cmd+L to toggle Gigabot)
   */
  useEffect(() => {
    if (!enableKeyboardShortcuts) return;

    const handleGlobalKeyDown = (e) => {
      if (e.key === KEY.L && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setShowGigabot((prev) => !prev);
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [enableKeyboardShortcuts]);

  return {
    // State
    showGigabot,
    setShowGigabot,
    context,

    // Data
    gigabotInitialMessage,
    gigabotContextData,
    gigabotAriaProps,

    // Handlers
    handleGigabotClose,
    handleGigabotOpen,
    handleActionRun,
    handleActionSkip,
    handleGigabotResize,
  };
};

export default useGigabotPanel;
